import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { type FleetDeviceDetailsPerDay } from 'types/fuelAnalytics';
import { getFuelUsageDataUnit } from '../utils';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { useMemo } from 'react';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  selectedSource: string[];
  height?: string;
  data: FleetDeviceDetailsPerDay;
  sortBy?: string;
  aggregation: string;
  getSourceLabel?: (source: string) => string;
}

const FuelAssetItemChart = ({
  selectedSource,
  height = '230px',
  data,
  aggregation,
  getSourceLabel = (source: string) =>
    capitalizeFirstLetter(source?.split('_').join(' ')),
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const primaryBarColor = palette.primary;
  const secondaryBarColor =
    CLIENT_THEME === 'yokohama' ? palette.grey : '#FA7B4A';
  const chartCursorColor =
    CLIENT_THEME === 'yokohama'
      ? palette.sectionBackground
      : enableDarkTheme
      ? '#1B2B4A'
      : '#CCCCCC';
  const { unitSystem } = useCustomerInfo();
  const data1Unit = useMemo(() => {
    const str = getFuelUsageDataUnit(selectedSource[0], unitSystem);
    return str ?? '';
  }, [selectedSource]);
  const data2Unit = useMemo(() => {
    if (selectedSource.length < 2) return '';
    const str = getFuelUsageDataUnit(selectedSource[1], unitSystem);
    return str ?? '';
  }, [selectedSource]);

  const format = useMemo(() => {
    return aggregation === 'Monthly' ? 'YYYY MMM' : DATE_FORMAT;
  }, [aggregation]);

  const formatTimestamp = (timestamp: string): string => {
    const formattedDate = convertStdDateString(timestamp, format);
    return formattedDate;
  };

  const { date_stats: dateStats } = data;

  const formattedDateLoad: any[] = useMemo(() => {
    return Object.keys(dateStats).map((date) => ({
      date,
      ...dateStats[date],
      avg_fuel_rate: Number(dateStats[date].avg_fuel_rate),
      loaded_fuel_consumption: Number(dateStats[date].loaded_fuel_consumption),
      num_of_idle_events: Number(dateStats[date].num_of_idle_events),
      total_duration_of_idle_events: Number(
        +dateStats[date].total_duration_of_idle_events.toFixed(2)
      ),
      consumption: Number(dateStats[date].total_fuel_consumption),
      unloaded_fuel_consumption: Number(
        dateStats[date].unloaded_fuel_consumption
      ),
      co2emission: Number(dateStats[date].co2_emission),
      fuel_consumed_per_ton: parseFloat(
        Number(dateStats[date].fuel_consumed_per_ton).toFixed(3)
      ),
      overspeeding: Number(dateStats[date].total_overspeed_fuel),
      underspeeding: Number(dateStats[date].total_underspeed_fuel),
      gears: Number(dateStats[date].total_gear_fuel),
      short_idling: Number(dateStats[date].total_short_idle_fuel),
      long_idling: Number(dateStats[date].total_long_idle_fuel),
      queuing:
        Number(dateStats[date].total_pre_loading_fuel ?? 0) +
        Number(dateStats[date].total_pre_dumping_fuel ?? 0),
    }));
  }, [dateStats]);

  const valid1Values = useMemo(() => {
    return formattedDateLoad
      .map((entry) =>
        Number(entry[selectedSource[0].toLowerCase().replace(/ /g, '_')])
      )
      .filter((value) => value > 0);
  }, [formattedDateLoad, selectedSource]);

  const valid2Values = useMemo(() => {
    if (selectedSource.length < 2) return [];
    return formattedDateLoad
      .map((entry) =>
        Number(entry[selectedSource[1].toLowerCase().replace(/ /g, '_')])
      )
      .filter((value) => value > 0);
  }, [formattedDateLoad, selectedSource]);

  const maxValue = Math.max(...valid1Values);
  const max2Value = Math.max(...valid2Values);
  formattedDateLoad.sort((a, b) => {
    const dateA = dayjs(a.date).unix();
    const dateB = dayjs(b.date).unix();
    return dateA - dateB;
  });

  return (
    <Box sx={{ paddingTop: '10px' }}>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height,
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={700}
            height={300}
            data={formattedDateLoad}
            margin={{
              top: 20,
              right: 100,
              left: 5,
              bottom: 5,
            }}
            barSize={30}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTimestamp}
              tick={{ fontSize: 10, fill: palette.textSecondary }}
            />
            <YAxis
              yAxisId={'left'}
              tick={{ fontSize: 10, fill: palette.textSecondary }}
              domain={[0, Math.ceil(maxValue * 1.1)]}
              label={{
                value: `${getSourceLabel(selectedSource[0])} ${data1Unit}`,
                angle: 270,
                position: 'insideLeft',
                fontSize: 10,
                textAnchor: 'middle',
                dy: 50,
                fill: palette.textSecondary,
              }}
              name={`${getSourceLabel(selectedSource[0])} ${data1Unit}`}
            />
            {selectedSource?.length > 1 && (
              <YAxis
                yAxisId={'right'}
                orientation="right"
                tick={{ fontSize: 10, fill: palette.textSecondary }}
                domain={[0, Math.ceil(max2Value * 1.1)]}
                label={{
                  value: `${getSourceLabel(selectedSource[1])} ${data2Unit}`,
                  angle: 270,
                  position: 'insideRight',
                  fontSize: 10,
                  textAnchor: 'middle',
                  dy: -50,
                  fill: palette.textSecondary,
                }}
                name={`${getSourceLabel(selectedSource[1])} ${data2Unit}`}
              />
            )}
            <Tooltip
              cursor={{ fill: chartCursorColor }}
              wrapperStyle={{ outline: 'none', fontSize: '11px' }}
              contentStyle={{
                backgroundColor: palette.boxBackground,
                border: `1px solid ${palette.cardBorderColor}`,
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                padding: '8px 12px',
              }}
              itemStyle={{
                color: palette.textPrimary,
                fontSize: '11px',
              }}
              labelStyle={{
                fontSize: '11px',
                fontWeight: '600',
                color: palette.textPrimary,
                marginBottom: '4px',
              }}
              labelFormatter={(value) => convertStdDateString(value, format)}
              formatter={(value: number) => {
                if (typeof value === 'number') {
                  return Number(value.toFixed(2));
                }
                return value;
              }}
            />
            <Bar
              yAxisId="left"
              name={`${getSourceLabel(selectedSource[0])} ${data1Unit}`}
              dataKey={selectedSource[0].toLowerCase().replace(/ /g, '_')}
              fill={primaryBarColor}
              activeBar={
                <Rectangle
                  fill={palette.primaryDark ?? primaryBarColor}
                  stroke={primaryBarColor}
                />
              }
            />
            {selectedSource?.length > 1 && (
              <Bar
                yAxisId="right"
                name={`${getSourceLabel(selectedSource[1])} ${data2Unit}`}
                dataKey={selectedSource[1].toLowerCase().replace(/ /g, '_')}
                fill={secondaryBarColor}
                activeBar={
                  <Rectangle
                    fill={palette.primaryDark ?? secondaryBarColor}
                    stroke={secondaryBarColor}
                  />
                }
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

export default FuelAssetItemChart;
